import * as XLSX from "xlsx";

const REGIONAL_TABS = ["Lahore", "Multan (2)", "Sahiwal (2)"];
const FINANCIAL_COLUMNS = ["Salary", "Mobile Allowance", "Fuel Allownace", "GLI", "Health Insurance", "EOBI", "Spring Commission", "Travelling Cost", "Samsung"];

function normalizeCode(value) {
  return String(value || "").replace(/\s+/g, "").toUpperCase();
}

function trimHeaders(row) {
  return Object.fromEntries(Object.entries(row).map(([key, value]) => [key.trim(), value]));
}

function money(value, column, code) {
  if (value === "" || value == null || value === "-") return 0;
  const number = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(number)) throw new Error(`${code}: invalid ${column} amount.`);
  return number;
}

export function parseCortevaWorkbook(buffer) {
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  if (!workbook.Sheets["Combined Salary"]) throw new Error("Corteva workbook needs a Combined Salary tab.");
  for (const tab of REGIONAL_TABS) {
    if (!workbook.Sheets[tab]) throw new Error(`Corteva workbook needs the ${tab} employee-details tab.`);
  }

  const salaryRows = XLSX.utils.sheet_to_json(workbook.Sheets["Combined Salary"], { defval: "" }).map(trimHeaders);
  if (!salaryRows.length) throw new Error("Combined Salary has no employee rows.");
  // A financial column (e.g. Spring Commission, Travelling Cost) can be
  // absent some months if nobody earned it that period — money() already
  // treats a missing/blank value as 0, and Days/Status already default via
  // `||` below, so there's nothing to block on. Only E.Code is load-bearing
  // (it identifies the employee) and is already enforced per-row further
  // down.

  const details = new Map();
  for (const tab of REGIONAL_TABS) {
    const regionalRows = XLSX.utils.sheet_to_json(workbook.Sheets[tab], { range: 3, defval: "" }).map(trimHeaders);
    for (const row of regionalRows) {
      const code = normalizeCode(row.Code);
      if (!/^C\d+$/i.test(code)) continue;
      if (details.has(code)) throw new Error(`Employee code ${code} appears on more than one regional tab.`);
      details.set(code, {
        sonOf: row["S/O"] || "",
        cnic: row["CNIC No."] || "",
        contact: row["Contact No."] || "",
        posting: row["Place of Posting"] || ""
      });
    }
  }

  const seen = new Set();
  const employees = salaryRows.filter((row) => normalizeCode(row["E.Code"])).map((row) => {
    const code = normalizeCode(row["E.Code"]);
    if (!/^C\d+$/i.test(code)) throw new Error(`Invalid Corteva employee code: ${code}.`);
    if (seen.has(code)) throw new Error(`Duplicate Corteva employee code: ${code}.`);
    seen.add(code);
    // Same reasoning as the missing-column case above: a new joiner not yet
    // added to their regional tab, or a leaver's final month, shouldn't
    // block the whole invoice — falls back to blank S/O, CNIC, contact,
    // posting for that one employee instead.
    const detail = details.get(code) || {};
    const amounts = Object.fromEntries(FINANCIAL_COLUMNS.map((column) => [column, money(row[column], column, code)]));
    const reimbursement = FINANCIAL_COLUMNS.reduce((sum, column) => sum + amounts[column], 0);
    return {
      code,
      name: String(row["Employee Name"] || "").trim(),
      ...detail,
      joined: row["D.O.J"] || "",
      days: Number(row.Days || 0),
      status: String(row.Status || "").trim(),
      salary: amounts.Salary,
      mobile: amounts["Mobile Allowance"],
      fuel: amounts["Fuel Allownace"],
      gli: amounts.GLI,
      health: amounts["Health Insurance"],
      eobi: amounts.EOBI,
      commission: amounts["Spring Commission"],
      travel: amounts["Travelling Cost"],
      samsung: amounts.Samsung,
      reimbursement,
      serviceFee: money(row["Service fee"], "Service fee", code)
    };
  });

  if (!employees.length) throw new Error("No Corteva employee codes were found in Combined Salary.");
  const reimbursement = Math.round(employees.reduce((sum, row) => sum + row.reimbursement, 0) * 100) / 100;
  const serviceCharges = employees.reduce((sum, row) => sum + row.serviceFee, 0);
  const gst = Math.round(serviceCharges * 0.16);
  const sumField = (field) => Math.round(employees.reduce((sum, row) => sum + row[field], 0) * 100) / 100;
  // Per-category totals behind the single `reimbursement` figure — kept
  // alongside it (not instead of it) so the invoice's category breakdown
  // (EOBI, health insurance, GLI, etc.) is visible in Monthly P&L instead of
  // collapsing into one "Other Reimbursement" line. Keyed by the normalized
  // LINE_CATEGORIES names (see lineCategories.js) so cortevaAdapter.js can
  // consume this the same way regardless of which Corteva workflow built it.
  const reimbursementBreakdown = {
    SALARY_REIMBURSEMENT: Math.round((sumField("salary") + sumField("mobile") + sumField("fuel") + sumField("commission") + sumField("travel") + sumField("samsung")) * 100) / 100,
    EOBI_RECOVERY: sumField("eobi"),
    INSURANCE_RECOVERY: sumField("health"),
    GLI_RECOVERY: sumField("gli")
  };
  return {
    employees,
    headCount: employees.length,
    reimbursement,
    reimbursementBreakdown,
    serviceCharges,
    gst,
    total: reimbursement + serviceCharges + gst
  };
}
